'use client';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState, useCallback } from 'react';
import { fetchWithCache, invalidateResource } from '@/lib/use-resource';
import { HealthPills } from './HealthPills';

const TABS: { href: string; label: string; matches: (p: string) => boolean }[] = [
  { href: '/', label: 'Visualizer', matches: (p) => p === '/' },
  { href: '/portfolio', label: 'Portfolio', matches: (p) => p.startsWith('/portfolio') },
  { href: '/alpha-zone', label: 'Alpha Zone', matches: (p) => p.startsWith('/alpha-zone') },
  { href: '/research-desk', label: 'Research', matches: (p) => p.startsWith('/research-desk') },
  { href: '/hedge-lab', label: 'Hedge Lab', matches: (p) => p.startsWith('/hedge-lab') },
  { href: '/playbook', label: 'Playbook', matches: (p) => p.startsWith('/playbook') },
  { href: '/pipeline', label: 'Pipeline', matches: (p) => p.startsWith('/pipeline') || p.startsWith('/architecture') },
  { href: '/presentation', label: 'Presentation', matches: (p) => p.startsWith('/presentation') },
  { href: '/rating', label: 'Rating', matches: (p) => p.startsWith('/rating') },
  // Temporary — calibration page for Claude-scored news A/B vs keyword feed.
  // Steve directive 2026-05-23. Remove this entry once we commit the scored
  // feed into /portfolio + /alpha-zone production.
  { href: '/news-ab', label: 'News A/B', matches: (p) => p.startsWith('/news-ab') },
];

type PillState = 'fresh-acked' | 'fresh-new' | 'stale' | 'in-progress';
const ACK_KEY = (href: string) => `appnav-ack:${href}`;

interface SurfacePayload {
  href?: string;
  status: 'ok' | 'stale' | 'missing' | 'empty';
  freshestAsOf: string | null;
}

interface PipelinePullsPayload {
  today: string;
  runs: Array<{ date: string; pulls: Record<string, { status: 'ok' | 'fail' | 'queued' }> }>;
  running: boolean;
}

export function AppNav() {
  const pathname = usePathname() || '/';
  const [pillStates, setPillStates] = useState<Record<string, PillState>>({});
  const [pillAsOf, setPillAsOf] = useState<Record<string, string>>({});
  const [systemHealth, setSystemHealth] = useState<'ok' | 'sapi-failure' | 'unknown'>('unknown');
  const [ciFailures, setCiFailures] = useState<{ repo: string; workflow: string; title: string; url: string }[]>([]);

  const [refreshing, setRefreshing] = useState(false);

  const refreshPills = useCallback(() => {
    Promise.all([
      fetchWithCache<{ surfaces?: SurfacePayload[] }>('/api/data-surfaces').catch(() => ({}) as { surfaces?: SurfacePayload[] }),
      fetchWithCache<PipelinePullsPayload>('/api/pipeline-status').catch(() => ({} as PipelinePullsPayload)),
    ])
      .then(([surfacesRes, pipelineRes]: [{ surfaces?: SurfacePayload[] }, PipelinePullsPayload]) => {
        const surfaces = surfacesRes?.surfaces ?? [];
        const states: Record<string, PillState> = {};
        const asOfs: Record<string, string> = {};

        // Global "refresh in flight" — Steve directive 2026-05-04: while
        // anything is being pulled (daily-refresh.log mtime in last 5 min,
        // OR any pull queued today), every dot in the system goes yellow
        // flashing. Green/red only after pulls complete.
        const todayRun = pipelineRes?.runs?.find(r => r.date === pipelineRes.today);
        const anyQueued = todayRun ? Object.values(todayRun.pulls).some(p => p.status === 'queued') : false;
        const anyFail = todayRun ? Object.values(todayRun.pulls).some(p => p.status === 'fail') : false;
        const isRefreshing = pipelineRes?.running === true || anyQueued;
        setRefreshing(isRefreshing);

        for (const tab of TABS) {
          const ours = surfaces.filter((s) => s.href === tab.href);
          if (ours.length === 0) continue;
          // Steve directive 2026-05-04: yellow covers both in-flight refresh
          // AND stale-but-not-failed. Red is reserved for actual failures
          // (missing artifact or empty file). A surface that's just `stale`
          // is waiting for its next scheduled pull — that's an in-progress
          // condition, not a failure.
          const anyHardFail = ours.some((s) => s.status === 'missing' || s.status === 'empty');
          const anyStale = ours.some((s) => s.status === 'stale');
          const latest = ours.reduce<string>(
            (acc, s) => (s.freshestAsOf && s.freshestAsOf > acc ? s.freshestAsOf : acc),
            ''
          );
          asOfs[tab.href] = latest;
          if (anyHardFail) {
            states[tab.href] = 'stale';
          } else if (isRefreshing || anyStale) {
            states[tab.href] = 'in-progress';
          } else {
            // Auto-ack: if the user is currently sitting on this pill's route,
            // record latest as the acked version so the pill doesn't flash at
            // a page they're already reading.
            if (typeof window !== 'undefined' && tab.matches(pathname) && latest) {
              localStorage.setItem(ACK_KEY(tab.href), latest);
            }
            const acked = typeof window !== 'undefined' ? localStorage.getItem(ACK_KEY(tab.href)) : null;
            states[tab.href] = acked === latest ? 'fresh-acked' : 'fresh-new';
          }
        }
        // Pipeline pill: pull-level statuses use a different vocabulary
        // (ok/queued/fail). 'fail' means dataPoints === 0 or artifact is
        // missing — a real failure → red. While refresh is in flight, the
        // global rule above already paints it yellow.
        if (!isRefreshing && anyFail) {
          states['/pipeline'] = 'stale';
        }
        setPillStates(states);
        setPillAsOf(asOfs);
      })
      .catch(() => { /* silent */ });
  }, [pathname]);

  useEffect(() => {
    refreshPills();
    // Poll every 60s. Invalidate first so we get a fresh fetch instead
    // of a cache hit; other consumers (DataAsOf, UnreadDot) will pick up
    // the new value the next time they re-render or refetch.
    const id = setInterval(() => {
      invalidateResource('/api/data-surfaces');
      invalidateResource('/api/pipeline-status');
      refreshPills();
    }, 60_000);
    return () => clearInterval(id);
  }, [refreshPills]);

  // Data-integrity is surfaced on the /pipeline page itself (DataIntegrityBanner)
  // — the section dedicated to finding problems — not as a red bar pinned atop
  // every page. Genuine critical failures still fire an iMessage via the
  // health-alert cron, so removing the global banner loses no real signal.

  useEffect(() => {
    fetchWithCache<{ status?: string; lastFailureTimestamp?: string; ciFailures?: { repo: string; workflow: string; title: string; url: string }[] }>('/api/system-health')
      .then((d) => {
        if (Array.isArray(d?.ciFailures)) setCiFailures(d.ciFailures);
        if (d?.status === 'sapi-failure') {
          const ts = d.lastFailureTimestamp ? Date.parse(d.lastFailureTimestamp) : NaN;
          if (Number.isFinite(ts) && Date.now() - ts < 24 * 3600 * 1000) {
            setSystemHealth('sapi-failure');
            return;
          }
        }
        setSystemHealth('ok');
      })
      .catch(() => setSystemHealth('unknown'));
  }, []);

  const ackPill = (href: string) => {
    const asOf = pillAsOf[href];
    if (!asOf) return;
    if (pillStates[href] !== 'fresh-new') return;
    localStorage.setItem(ACK_KEY(href), asOf);
    setPillStates((prev) => ({ ...prev, [href]: 'fresh-acked' }));
  };

  return (
    <div className="sticky top-0 z-50 flex-shrink-0 print:hidden">
      <nav className="border-b border-[#1e293b] bg-[#0a0e14] flex items-center gap-4 px-5 py-2 text-[12px]">
      <div className="flex items-center gap-2 mr-3">
        <div
          className={`w-2 h-2 rounded-full ${
            systemHealth === 'sapi-failure'
              ? 'bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.7)] animate-pulse'
              : refreshing
                ? 'bg-amber-300 shadow-[0_0_8px_rgba(252,211,77,0.9)] animate-pulse'
                : 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]'
          }`}
          title={
            systemHealth === 'sapi-failure'
              ? 'SAPI fetch failed in the last 24h — check data/system-health.json'
              : refreshing
                ? 'Refresh in progress — data is being pulled'
                : 'System healthy'
          }
        />
        <span className="text-white font-bold tracking-tight">Triumph</span>
        {ciFailures.length > 0 && pathname.startsWith('/pipeline') && (
          <a
            href={ciFailures[0].url}
            target="_blank"
            rel="noopener"
            className="ml-2 flex items-center gap-1 px-2 py-0.5 rounded-md border border-red-400/60 bg-red-400/10 text-red-300 text-[10px] font-bold tracking-wide uppercase hover:bg-red-400/20 transition-colors animate-pulse"
            title={ciFailures.map((f) => `${f.repo.split('/')[1]}: ${f.title}`).join('\n')}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.9)]" />
            CI {ciFailures.length}
          </a>
        )}
      </div>
      <div className="flex items-center gap-1">
        {TABS.map((tab) => {
          const active = tab.matches(pathname);
          const state = pillStates[tab.href];
          const dotClass =
            state === 'stale'
              ? 'bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.9)] animate-pulse'
              : state === 'in-progress'
                ? 'bg-amber-300 shadow-[0_0_8px_rgba(252,211,77,0.9)] animate-pulse'
                : state === 'fresh-new'
                  ? 'bg-emerald-400 shadow-[0_0_8px_rgba(74,222,128,0.9)] animate-pulse'
                  : state === 'fresh-acked'
                    ? 'bg-emerald-400/70'
                    : '';
          const dotTitle =
            state === 'stale'
              ? `${tab.label} data is stale or failed — check /pipeline`
              : state === 'in-progress'
                ? `${tab.label} is partially done — refresh in flight or some pulls queued`
                : state === 'fresh-new'
                  ? `${tab.label} has fresh data (click to acknowledge)`
                  : state === 'fresh-acked'
                    ? `${tab.label} data is fresh`
                    : '';
          return (
            <Link
              key={tab.href}
              href={tab.href}
              onClick={() => ackPill(tab.href)}
              className={`relative px-3 py-1 pb-2 rounded-md font-semibold tracking-wide uppercase text-[11px] transition-colors ${
                active
                  ? 'bg-amber-400/20 text-amber-300 border border-amber-400/40'
                  : 'text-[#94a3b8] hover:text-white border border-transparent hover:border-[#1e293b]'
              }`}
              title={dotTitle || undefined}
            >
              {tab.label}
              {dotClass && (
                <span
                  className={`absolute left-1/2 -translate-x-1/2 bottom-0.5 w-1.5 h-1.5 rounded-full ${dotClass}`}
                  aria-hidden
                />
              )}
            </Link>
          );
        })}
      </div>
      <div className="ml-auto">
        <HealthPills />
      </div>
      </nav>
    </div>
  );
}
